import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, ConfirmModal, showToast, Spinner } from "../shared";
import EditTeamModal from "./EditTeamModal";
import TransferOwnershipModal from "./TransferOwnershipModal";
import MoveProjectModal from "../Projects/MoveProjectModal";
import { useDeleteTeam } from "../../lib/react-query/teams";
import { useProjectsList } from "../../lib/react-query/projects";

const StatBox = ({ label, value, borderColor }) => (
  <div className={`border rounded-lg p-3 border-l-2 bg-white ${borderColor}`}>
    <p className="text-xs text-grey">{label}</p>
    <p className="text-xl font-semibold text-darkgrey">{value}</p>
  </div>
);

const TeamSettingsSection = ({ teamId, team, members, canManageTeam, currentUserId }) => {
  const navigate = useNavigate();
  const { mutateAsync: deleteTeam, isLoading: isDeletingTeam } = useDeleteTeam();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [moveProject, setMoveProject] = useState(null);

  const isOwner = currentUserId === team?.owner;

  const { data: projectsData, isLoading: projectsLoading } = useProjectsList(
    { team_id: teamId, page_size: 100 },
    !!teamId
  );
  const projects = projectsData?.items || [];

  const ownerName = (() => {
    if (!Array.isArray(members) || !team?.owner) return null;
    const owner = members.find((member) => member?.user?.id === team.owner);
    return owner?.user?.full_name || owner?.user?.email || `User #${team.owner}`;
  })();

  const isPersonalTeam = team?.is_personal === true;

  const handleDeleteTeam = async () => {
    try {
      await deleteTeam({ teamId });
      showToast("Team deleted successfully", "success", { position: "top-center" });
      setDeleteModalOpen(false);
      navigate("/teams", { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to delete team.";
      showToast(msg, "error", { position: "top-center" });
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl p-4 border border-surface-border">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatBox label="Team name" value={team.name || "—"} borderColor="border-l-secondary/60" />
          <StatBox
            label="Type"
            value={isPersonalTeam ? "Personal workspace" : "Shared team"}
            borderColor="border-l-primary/40"
          />
          <StatBox label="Owner" value={ownerName || `User #${team.owner}`} borderColor="border-l-warning/60" />
        </div>
      </div>

      <div className="rounded-xl border border-surface-border bg-surface/60 p-4 text-sm text-grey">
        {isPersonalTeam
          ? "Personal workspaces are created automatically for each account. They are best for private work. Create a shared team when you want invites, collaborative ownership, and a workspace managed by multiple people."
          : "Shared teams are collaborative workspaces. You can invite members, transfer ownership, and manage the workspace together."}
      </div>

      <section className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border bg-surface-hover/50">
          <h2 className="text-base font-semibold text-darkgrey">Settings</h2>
        </div>

        <div className="divide-y divide-surface-border">
          <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-darkgrey">{isPersonalTeam ? "Workspace name" : "Team name"}</p>
              <p className="text-xs text-mediumgrey mt-0.5">{team.name}</p>
            </div>
            {canManageTeam && (
              <Button
                variant="outlined"
                color="secondary"
                size="sm"
                onClick={() => setEditModalOpen(true)}
              >
                Edit
              </Button>
            )}
          </div>

          <div className="px-5 py-4">
            <p className="text-sm font-medium text-darkgrey">Description</p>
            <p className="text-xs text-mediumgrey mt-0.5">
              {team.description || <span className="italic">No description</span>}
            </p>
          </div>

          <div className="px-5 py-4">
            <p className="text-sm font-medium text-darkgrey">Owner</p>
            <p className="text-xs text-mediumgrey mt-0.5">{ownerName || `User #${team.owner}`}</p>
          </div>

          {canManageTeam && !isPersonalTeam && (
            <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-darkgrey">Transfer ownership</p>
                <p className="text-xs text-mediumgrey mt-0.5">
                  Move primary control of this team to another current member. You will become an admin.
                </p>
              </div>
              <Button
                variant="outlined"
                color="danger"
                size="sm"
                onClick={() => setTransferModalOpen(true)}
              >
                Transfer ownership
              </Button>
            </div>
          )}

          {canManageTeam && !isPersonalTeam && (
            <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-darkgrey">Delete team</p>
                <p className="text-xs text-mediumgrey mt-0.5">
                  Permanently remove this shared team and return to your team list.
                </p>
              </div>
              <Button
                variant="outlined"
                color="danger"
                size="sm"
                onClick={() => setDeleteModalOpen(true)}
              >
                Delete team
              </Button>
            </div>
          )}
        </div>
      </section>

      {isOwner && (
        <section className="bg-white rounded-xl border border-surface-border overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border bg-surface-hover/50">
            <h2 className="text-base font-semibold text-darkgrey">
              Projects in this workspace
            </h2>
          </div>

          <div className="divide-y divide-surface-border">
            {projectsLoading ? (
              <div className="px-5 py-6 flex justify-center">
                <Spinner />
              </div>
            ) : projects.length === 0 ? (
              <div className="px-5 py-4 text-sm text-grey">
                No projects in this workspace yet.
              </div>
            ) : (
              projects.map((project) => (
                <div
                  key={project.id}
                  className="px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                >
                  <div>
                    <p className="text-sm font-medium text-darkgrey">
                      {project.title}
                    </p>
                    <p className="text-xs text-mediumgrey mt-0.5">
                      {project.status || "Active"}
                    </p>
                  </div>
                  {project.owner?.id === currentUserId && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="md"
                      className="min-h-[44px]"
                      onClick={() => setMoveProject(project)}
                    >
                      Move
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {!canManageTeam && (
        <p className="text-xs text-grey">
          Contact the team owner to update these settings.
        </p>
      )}

      <EditTeamModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        teamId={teamId}
        team={team}
      />
      <TransferOwnershipModal
        isOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        teamId={teamId}
        members={members}
        currentUserId={currentUserId}
      />
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteTeam}
        title="Delete team"
        message="Are you sure you want to delete this shared team? Members will lose access to the workspace and its shared resources."
        confirmText="Delete team"
        confirmColor="danger"
        isLoading={isDeletingTeam}
      />
      {moveProject && (
        <MoveProjectModal
          isOpen={!!moveProject}
          onClose={() => setMoveProject(null)}
          project={moveProject}
          currentTeamId={teamId}
        />
      )}
    </div>
  );
};

export default TeamSettingsSection;
